// src/components/messaging/MessagesPage.tsx - COMPLETE with React Router
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMessages } from '../../hooks/useMessages';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';

interface MessagesPageProps {
  onNavigate: (page: string) => void;
}

const MessagesPage: React.FC<MessagesPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const navigation = useNavigation();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Track processed userIds to prevent duplicate processing
  const processedUserIdRef = useRef<string | null>(null);
  // Phase 2: Track conversation state to prevent repeated loadMessages calls
  const lastConversationIdRef = useRef<string | null>(null);
  const lastConversationsLengthRef = useRef<number>(0);
  
  const { 
    conversations, 
    activeConversation, 
    messages, 
    loading, 
    error, 
    loadConversations,
    loadMessages,
    setActiveConversationById,
    createConversation,
    clearError,
    setError,
    setConversations,
    setActiveConversation,
    setMessages,
    appendIncomingMessage,
    sendMessage,
    markAsRead
  } = useMessages();
  
  const { connect, disconnect, isConnected, connectionStatus, error: wsError, onMessage, sendMessage: sendWebSocketMessage } = useWebSocket();
  const [isMobile, setIsMobile] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Attempt to reconnect if disconnected
      if (!isConnected && user) {
        connect();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setError('You are currently offline. Please check your internet connection.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, user?.id]); // Only depend on user.id, not function references

  // Load conversations and connect WebSocket on mount
  // Only run once when user is available, not on every dependency change
  useEffect(() => {
    if (user) {
      loadConversations();
      connect();
    }
    
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user.id, not the entire user object or function references

  // Phase 2: Handle URL parameter for specific conversation
  // Fixed to prevent repeated loadMessages calls when conversations array updates from WebSocket events
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      // Only call if conversationId changed OR conversations just loaded (length changed from 0)
      const conversationIdChanged = conversationId !== lastConversationIdRef.current;
      const conversationsJustLoaded = conversations.length !== lastConversationsLengthRef.current && lastConversationsLengthRef.current === 0;
      
      if (conversationIdChanged || conversationsJustLoaded) {
        // Guard: Don't call if already active (prevents unnecessary loadMessages call)
        if (activeConversation?.id !== conversationId) {
          setActiveConversationById(conversationId);
        }
        lastConversationIdRef.current = conversationId;
      }
      lastConversationsLengthRef.current = conversations.length;
    }
  }, [conversationId, conversations.length, setActiveConversationById, activeConversation?.id]); // Use conversations.length, not conversations array

  // Join active conversation room when connected or when activeConversation changes
  useEffect(() => {
    if (isConnected && activeConversation?.id) {
      sendWebSocketMessage({ type: 'conversation:join', data: { conversationId: activeConversation.id } });
    }
  }, [isConnected, activeConversation?.id, sendWebSocketMessage]);

  // Register real-time event handlers
  useEffect(() => {
    // message:new forwarded as 'message' by useWebSocket
    const handleIncomingMessage = (envelope: any) => {
      const conversationIdFromEvent = envelope?.conversationId || envelope?.message?.conversationId;
      const rawMessage = envelope?.message || envelope;
      if (!conversationIdFromEvent || !rawMessage) return;

      // Map backend message format to frontend format
      const currentUserId = user?.id || '';
      const mappedMessage = {
        id: rawMessage._id?.toString() || rawMessage.id?.toString() || '',
        conversationId: conversationIdFromEvent.toString(),
        senderId: rawMessage.senderId?.toString() || rawMessage.senderId || '',
        content: rawMessage.content || '',
        timestamp: rawMessage.createdAt || rawMessage.timestamp || new Date().toISOString(),
        status: {
          sent: true,
          delivered: rawMessage.status?.deliveredTo?.length > 0 || false,
          read: rawMessage.status?.readBy?.some((r: any) => 
            (r.userId?.toString() || r.userId) === currentUserId
          ) || false,
          readAt: rawMessage.status?.readBy?.find((r: any) => 
            (r.userId?.toString() || r.userId) === currentUserId
          )?.readAt || undefined,
        },
        type: rawMessage.type || 'text',
        replyTo: rawMessage.replyTo?.toString() || rawMessage.replyTo || undefined,
      };

      // Update messages if this is the active conversation
      if (activeConversation?.id && activeConversation.id === conversationIdFromEvent) {
        // Check if we have an optimistic message that matches this one
        // Match by: same conversation, same sender, same content, similar timestamp (within 5 seconds)
        const optimisticMessage = messages.find(msg => 
          msg.id.startsWith('temp-') && // Is optimistic
          msg.conversationId === conversationIdFromEvent &&
          msg.senderId === mappedMessage.senderId &&
          msg.content === mappedMessage.content &&
          Math.abs(new Date(msg.timestamp).getTime() - new Date(mappedMessage.timestamp).getTime()) < 5000 // Within 5 seconds
        );
        
        if (optimisticMessage) {
          // Replace optimistic message with real one from WebSocket
          setMessages(prev => prev.map(msg => 
            msg.id === optimisticMessage.id ? mappedMessage : msg
          ));
        } else {
          // No optimistic message match - add normally (with duplicate check)
          appendIncomingMessage(mappedMessage);
        }
      }

      // Update conversation list lastMessage and bump updatedAt
      setConversations(prev => {
        const exists = prev.some(c => c.id === conversationIdFromEvent);
        if (!exists) return prev;
        return prev.map(c => 
          c.id === conversationIdFromEvent
            ? {
                ...c,
                lastMessage: {
                  content: mappedMessage.content,
                  timestamp: mappedMessage.timestamp,
                  senderId: mappedMessage.senderId
                },
                updatedAt: mappedMessage.timestamp
              }
            : c
        );
      });
    };

    const handleConversationUpdated = (payload: any) => {
      const conversationIdFromEvent = payload?.conversationId;
      const last = payload?.lastMessage;
      if (!conversationIdFromEvent || !last) return;
      setConversations(prev => prev.map(c => 
        c.id === conversationIdFromEvent
          ? {
              ...c,
              lastMessage: {
                content: last.content,
                timestamp: last.timestamp,
                senderId: last.senderId
              },
              updatedAt: last.timestamp
            }
          : c
      ));
    };

    const handleReadReceipt = (payload: any) => {
      const { conversationId: convId, messageId, status: updatedStatus } = payload || {};
      if (!convId || !messageId || !updatedStatus) return;
      
      // Update message status in the active conversation's message list
      if (activeConversation?.id === convId) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, status: { ...msg.status, ...updatedStatus } }
            : msg
        ));
      }
      
      // Also update in conversation list if needed (for unread count)
      setConversations(prev => prev.map(conv => {
        if (conv.id === convId && updatedStatus.read) {
          // Decrement unread count when message is read
          return { ...conv, unreadCount: Math.max(0, conv.unreadCount - 1) };
        }
        return conv;
      }));
    };

    onMessage('message', handleIncomingMessage);
    onMessage('conversation_update', handleConversationUpdated);
    onMessage('read_receipt', handleReadReceipt);

    return () => {
      // No explicit offMessage in this file; useWebSocket provides offMessage if needed
    };
  }, [activeConversation?.id, messages, onMessage, setConversations, setMessages, appendIncomingMessage, user?.id]);

  // Handle userId query parameter to create/open conversation
  useEffect(() => {
    const userId = searchParams.get('userId');
    // Wait for conversations to load, then process userId
    // Allow processing even if conversations is empty (will create new conversation)
    // Skip if already processed to prevent duplicates
    if (!userId || loading || processedUserIdRef.current === userId) {
      return;
    }

    // Validate MongoDB ObjectId format (24 hex characters)
    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(userId);
    
    if (!isValidObjectId) {
      setError(`Invalid user ID format. The user ID must be a valid MongoDB ObjectId (24 hex characters). Received: ${userId}. This may be because you're using mock match data. Please use a real match from the backend API.`);
      // Clear the userId param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('userId');
      setSearchParams(newParams);
      return;
    }

    // Get match user data from URL params (if coming from matches page)
    const matchUserData = {
      firstName: searchParams.get('firstName') || undefined,
      lastName: searchParams.get('lastName') || undefined,
      profilePicture: searchParams.get('profilePicture') || undefined,
    };

    // Check if conversation already exists with this user
    // Normalize userId for comparison
    const normalizedUserId = userId.trim().toLowerCase();
    const existingConversation = conversations.length > 0 ? conversations.find(conv => {
      const participantId = conv.participant.id?.toString() || conv.participant.id;
      const normalizedParticipantId = participantId.trim().toLowerCase();
      return normalizedParticipantId === normalizedUserId;
    }) : null;

    // Mark as processing to prevent duplicate execution
    processedUserIdRef.current = userId;

    if (existingConversation) {
      // Update existing conversation with match user data if available
      const updatedConversation = (matchUserData.firstName || matchUserData.lastName || matchUserData.profilePicture) ? {
        ...existingConversation,
        participant: {
          ...existingConversation.participant,
          firstName: matchUserData.firstName || existingConversation.participant.firstName,
          lastName: matchUserData.lastName || existingConversation.participant.lastName,
          profilePicture: matchUserData.profilePicture || existingConversation.participant.profilePicture,
        },
      } : existingConversation;

      // Update conversations list with match data (if provided)
      if (matchUserData.firstName || matchUserData.lastName || matchUserData.profilePicture) {
        setConversations(prev => prev.map(conv => 
          conv.id === existingConversation.id ? updatedConversation : conv
        ));
      }

      // Set as active conversation and load messages
      setActiveConversation(updatedConversation);
      loadMessages(existingConversation.id);
      // Clear the query params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('userId');
      newParams.delete('firstName');
      newParams.delete('lastName');
      newParams.delete('profilePicture');
      setSearchParams(newParams);
      // Reset processed flag after params cleared
      processedUserIdRef.current = null;
    } else {
      // Create new conversation with match user data
      createConversation(userId, undefined, matchUserData).then(result => {
        if (result.success) {
          // Clear the query params after successful creation
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('userId');
          newParams.delete('firstName');
          newParams.delete('lastName');
          newParams.delete('profilePicture');
          setSearchParams(newParams);
          // Reset processed flag after params cleared
          processedUserIdRef.current = null;
        } else {
          // Reset flag on failure so it can be retried
          processedUserIdRef.current = null;
        }
      });
    }
  }, [searchParams, conversations, loading, createConversation, setActiveConversation, loadMessages, setSearchParams, setError, setConversations, setActiveConversationById]);

  const handleBackToDashboard = () => {
    try {
      navigation.goToDashboard();
    } catch {
      onNavigate('dashboard');
    }
  };

  const handleConversationSelect = (conversationId: string) => {
    setActiveConversationById(conversationId);
    // Update URL to reflect selected conversation
    try {
      navigation.goToConversation(conversationId);
    } catch {
      onNavigate(`messages/${conversationId}`);
    }
  };


  if (loading && conversations.length === 0) {
    return (
      <div className="min-h-screen bg-paper relative">
        <BackgroundShapes />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-coffee text-xl">Loading conversations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-coffee mb-2">
              Messages
            </h1>
            <p className="text-coffee/70">
              {conversations.length} conversations • {isConnected ? 'Connected' : 'Connecting...'}
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleBackToDashboard}
              className="bg-steel text-white border-2 border-coffee rounded-lg shadow-sm hover:shadow-md transition-all px-4 py-2 font-semibold"
            >
              ← Dashboard
            </button>
          </div>
        </div>

        {/* Error State */}
        {(error || wsError || !isOnline) && (
          <div className={`rounded-xl p-4 mb-8 border-2 ${
            !isOnline 
              ? 'bg-yellow-50 border-yellow-400'
              : 'bg-red-50 border-red-400'
          }`}>
            <div className={`${!isOnline ? 'text-yellow-800' : 'text-red-800'}`}>
              {!isOnline 
                ? '⚠️ You are currently offline. Please check your internet connection.'
                : wsError || error || 'An error occurred'
              }
            </div>
            {!isOnline ? (
              <div className="text-yellow-700 text-sm mt-2">
                Messages will sync when you're back online.
              </div>
            ) : (
              <button 
                onClick={clearError}
                className="text-red-700 hover:text-red-800 text-sm mt-2 underline"
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {/* Connection Status Banner */}
        {connectionStatus === 'connecting' && (
          <div className="bg-steel/20 border-2 border-steel rounded-xl p-3 mb-4">
            <div className="text-coffee text-sm">
              🔄 Connecting to server...
            </div>
          </div>
        )}
        
        {connectionStatus === 'error' && wsError && (
          <div className="bg-terracotta/20 border-2 border-terracotta rounded-xl p-3 mb-4">
            <div className="text-coffee text-sm">
              ⚠️ {wsError}
            </div>
            <button
              onClick={() => connect()}
              className="text-terracotta hover:underline text-sm mt-2"
            >
              Try reconnecting
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-paper border-2 border-coffee rounded-xl shadow-[4px_4px_0px_#594A42] overflow-hidden">
          <div className="flex h-[600px]">
            {/* Desktop: Side-by-side layout */}
            {!isMobile && (
              <>
                {/* Conversation List */}
                <div className="w-1/3 border-r-2 border-coffee" style={{ backgroundColor: '#F2EFE5' }}>
                  <ConversationList
                    conversations={conversations}
                    activeConversation={activeConversation}
                    onConversationSelect={handleConversationSelect}
                  />
                </div>

                {/* Chat Window */}
                <div className="w-2/3">
                  <ChatWindow
                    activeConversation={activeConversation}
                    messages={messages}
                    loading={loading}
                    onNavigate={onNavigate}
                    sendMessage={sendMessage}
                    markAsRead={markAsRead}
                  />
                </div>
              </>
            )}

            {/* Mobile: Single view */}
            {isMobile && (
              <>
                {!activeConversation ? (
                  <ConversationList
                    conversations={conversations}
                    activeConversation={activeConversation}
                    onConversationSelect={handleConversationSelect}
                  />
                ) : (
                  <ChatWindow
                    activeConversation={activeConversation}
                    messages={messages}
                    loading={loading}
                    onNavigate={onNavigate}
                    onBack={() => setActiveConversationById('')}
                    sendMessage={sendMessage}
                    markAsRead={markAsRead}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;